<template>
    <!-- Dropdown listauksen sulkeminen / klikkauslistan ulkopuolella käsitellä click-outside directiivillä -->
    <div
        v-click-outside="closeSelectList"
        class="dropdownelement"
    >
        <div
            ref="areaSelectionDropdown"
            class="dropdown"
        >
            <div
                class="custom-select"
                :class="{empty: !listState}"
            >
                <input
                    :id="inputId + '-searchinput'"
                    :ref="inputId + '-search'"
                    v-model="searchValue"
                    :disabled="disabled"
                    type="text"
                    class="default empty"
                    role="combobox"
                    aria-autocomplete="list"
                    :aria-controls="'div-' + inputId"
                    :aria-label="searchAriaLabel"
                    :aria-expanded="isListOpen"
                    :placeholder="searchPlaceholder"
                    @click="handleSelect"
                    @keyup.esc="escClose"
                    @keydown.enter.prevent="handleSelect"
                    @keydown.down.prevent="focusNextOption"
                    @keydown.up.prevent="focusPreviousOption"
                    @input="searchItems()"
                >
            </div>
            <div
                v-show="listState"
                class="selectlistwrapper"
            >
                <!-- Alasveto valikko jossa valittavat tiedot -->
                <ul
                    :id="'div-' + inputId"
                    class="dropdown-selectlist"
                    role="listbox"
                    :style="{ maxHeight: maxHeight + 'px' }"
                >
                    <li
                        v-for="(value, index) in itemsForUi"
                        :id="'option-' + index"
                        :key="index"
                        ref="listItems"
                        :class="{&quot;active-item&quot;: currentItem === index, &quot;selectlist-content&quot;: 1 === 1 }"
                        role="option"
                        :aria-selected="currentItem === index"
                        @click="selectValue(value, $event)"
                    >
                        {{ $t(value.labelText) }}
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { Vue, Component, Prop, Watch } from "vue-facing-decorator"
    import DropDownListModel from "@/models/dropdownlistmodel"

    /** Select/Dropdownkomponentti Search toiminnolla */
    @Component
    export default class SelectListWithSearch extends Vue {
        /** Näytettävä lista */
        @Prop({ type: Array }) readonly list: Array<DropDownListModel> | undefined
        /** Komponenttin tunniste */
        @Prop({ type: String }) readonly inputId: string | undefined
        /** Komponenttin selväkielinen tunniste esim. kunta, maakunta  */
        @Prop({ type: String }) readonly inputName: string | undefined
        /** Näytettävä placeholder */
        @Prop({ type: String }) readonly placeholder: string | undefined
        /** komponentin disablointi */
        @Prop({ type: Boolean }) readonly disabled: boolean | undefined
        /** Valittu arvo listalta */
        @Prop({ type: DropDownListModel }) selectedListValue!: DropDownListModel | undefined

        currentItem: number = -1
        listState: boolean = false
        selectedDropDownListModel: DropDownListModel = DropDownListModel.createEmpty()
        maxHeight: number = 0
        filteredItems: Array<DropDownListModel> | undefined = []
        ticks = 0
        searchQuery = ""
        searchValue = ""

        mounted(): void {
            this.selectedDropDownListModel = this.selectedListValue ?? DropDownListModel.createEmpty()
            this.filteredItems = this.list
        }

        get isListOpen(): string {
            // Pitää palauttaa boolean stringinä jotta toimii
            if (this.listState) {
                return "true"
            }
            else {
                return "false"
            }
        }

        handleSelect(): void {
            this.searchItems()
            this.listState = !this.listState
            if (!this.listState) {
                // set maxheight
                this.maxHeight = window.innerHeight - (this.$el as any).getBoundingClientRect().top - 150
            }

            if (this.filteredItems && this.currentItem !== -1) {
                this.selectValue(this.filteredItems[this.currentItem], null)
            }

            if (!this.listState) {
                this.currentItem = -1
            }
        }

        focusNextOption() {
            this.listState = true

            if (this.filteredItems) {
                this.currentItem = (this.currentItem + 1) % this.filteredItems.length
                this.scrollToActiveItem()
            }
        }

        focusPreviousOption() {
            this.listState = true
            if (this.filteredItems) {
                this.currentItem = (this.currentItem - 1 + this.filteredItems.length) % this.filteredItems.length
                this.scrollToActiveItem()
            }
        }

        scrollToActiveItem() {
            const listItems = this.$refs.listItems as HTMLElement

            // Scroll the highlighted item into view
            if (this.currentItem > -1 && listItems[this.currentItem]) {
                listItems[this.currentItem].scrollIntoView({
                    block: "nearest",
                    inline: "nearest"
                })
            }
        }

        @Watch("selectedListValueGetter")
        setInputSelectedValue() {
            this.selectedDropDownListModel = this.selectedListValue ?? DropDownListModel.createEmpty()
            this.currentItem = this.filteredItems?.indexOf(this.selectedDropDownListModel) ?? -1
            this.searchValue = this.selectedDropDownListModel.labelText
        }

        @Watch("activeLang")
        async onActiveLang(): Promise<void> {
            // haetaan valitun kohteen oikea nimi -> kieli on voitu vaihtaa sen jälkeen kun esim kunta on valittu.
            this.searchValue = this.list?.find(x => x.identifier === this.selectedDropDownListModel?.identifier)?.labelText ?? ""
        }

        /** jos valikko on auki niin piilotaan placeholder teksti */
        get getPlaceholder(): string {
            return this.placeholder ?? ""
        }

        /** tämä mukana jos kartalta valitaan esim. kunta/maakunta niin selectedlistValue muuttuu mutta jostain syystä muutos ei näy selectedDropDownListModelissa*/
        get selectedListValueGetter(): DropDownListModel {
            return this.selectedListValue ?? DropDownListModel.createEmpty()
        }

        get searchPlaceholder(): string | undefined {
            if (this.listState) {
                return this.$t("App.SearchSelectList.SearchPlaceholder") ?? ""
            }
            return this.$t(this.getPlaceholder) ?? ""
        }

        get searchAriaLabel(): string | undefined {
            return this.$t("App.RadioButtonListHeader.ValitseAlue") ?? ""
        }

        get itemsForUi(): Array<DropDownListModel> | undefined {
            return this.filteredItems
        }

        closeSelectList(): void {
            this.searchQuery = ""
            this.searchItems()
            this.listState = false
        }

        // handle closing dropdown when esc -key is pressed
        escClose() {
            this.closeSelectList()
        }

        selectValue(value: DropDownListModel): void {
            this.selectedDropDownListModel = value
            this.currentItem = this.filteredItems?.indexOf(value) ?? 0
            this.searchValue = this.selectedDropDownListModel.labelText
            this.$emit("valueselected", { dropdownItem: value, name: this.inputName })
            this.listState = false
        }

        searchItems(): void {
            if (!this.listState) {
                this.currentItem = -1
            }
            else if (this.searchValue) {
                this.currentItem = 0
            }
            if (this.searchValue) {
                this.listState = true
            }
            this.searchQuery = this.searchValue
            if (!this.searchQuery || !this.list) {
                this.filteredItems = this.list ?? []
                return
            }

            const foundItems: Array<DropDownListModel> | undefined = []

            for (const item of this.list) {
                if (this.matchesWith(item.labelText ?? "", this.searchQuery)) {
                    foundItems.push(item)
                }
            }

            // update the list
            this.filteredItems = foundItems
        }

        matchesWith(value1: string, value2: string): boolean {
            const lowkeyValue1 = value1.toLowerCase().trim()
            const lowkeyValue2 = value2.toLowerCase().trim()

            if (lowkeyValue1.indexOf(lowkeyValue2) > -1) {
                return true
            }

            return false
        }
    }

</script>

<style scoped>

    .active-item {
        color: #ffffff;
        background-color: #1e90ff;
        width: 100%;
    }

    ul {
        list-style-type: none;
        margin-bottom: 0;
    }

        ul li {
            padding-left: 0.5rem;
            cursor: pointer;
        }

    div[disabled=disabled] {
        pointer-events: none;
        color: #939393;
        border-color: #939393;
        background-color: #EDF0F5;
        outline: 1px solid #939393;
    }

    .custom-select {
        position: relative;
        width: 100%;
        height: auto;
        text-align: center;
        border: 1px solid black;
        border-radius: 2px;
        box-shadow: 0px 0px 0px 2px #000000 inset;
    }

        .custom-select:focus {
            border: 1px solid black;
            border-radius: 2px;
            box-shadow: 0px 0px 0px 2px #000000 inset;
        }

        .custom-select.empty {
            outline: none;
            border: none;
            box-shadow: none;
        }

    input.default.empty {
        width: 100%;
    }

    .selectedValuesContainer {
        display: flex;
        flex-direction: row;
        flex-flow: wrap;
    }

    .selectedValues {
        background-color: var(--Traficom-main-color);
        color: #ffffff;
        padding: 0.54rem 1rem 0.54rem 1rem;
        margin: 4px;
        cursor: pointer;
        user-select: none;
    }

    .dropdownelement {
        margin: 0 0 12px 0;
    }

    .dropdown {
        width: 100%;
        position: relative;
        margin-top: 0.9rem;
        margin-bottom: 1.2rem;
    }

    .dropdown-selectlist {
        background-color: white;
        padding: 0;
        width: inherit;
        overflow-y: auto;
        overflow-x: hidden;
        min-height: 300px;
    }

    div > div.dropdown::after {
        position: absolute;
        right: 0.625rem;
        top: 0.75rem;
        filter: invert(10%) sepia(27%) saturate(5527%) hue-rotate(205deg) brightness(96%) contrast(106%);
    }

    .checkbox-list-element .container {
        margin: 0;
        padding: 0;
    }

    .selectlistwrapper {
        border: solid 1px black;
        position: absolute;
        z-index: 1005;
        width: inherit;
        overflow-y: auto;
        overflow-x: hidden;
    }

    .search-wrapper {
        padding: 10px;
        background-color: white;
    }

    input.search.search-selectlist {
        width: calc(100% - 50px); /* searchbutton on 50px */
    }

    .checkbox-select-list-selectlist .checkbox-select-list-checkbox .selectlist-content {
        display: inline-block;
        margin-top: 0.5rem; /* same margin than in the input checkbox next to it. */
    }

    label.checkbox {
        padding-left: 10px;
    }
</style>
